import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import {
	Drawer,
	Typography,
	IconButton,
	Button,
	Divider,
	Tooltip,
	CircularProgress,
	LinearProgress,
	Chip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import DesktopWindowsIcon from "@material-ui/icons/DesktopWindows";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import AccountBalanceWalletOutlinedIcon from "@material-ui/icons/AccountBalanceWalletOutlined";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import DeviceId from "../FlydeskDevices/DeviceId";
import StatusDot from "../FlydeskDevices/StatusDot";

const ONLINE_WINDOW_MS = 2 * 60 * 1000;
const isOnline = (v) =>
	Boolean(v) && Date.now() - new Date(v).getTime() < ONLINE_WINDOW_MS;

const MODULES = [
	{ key: "whatsapp", icon: WhatsAppIcon },
	{ key: "financeiro", icon: AccountBalanceWalletOutlinedIcon },
	{ key: "flydesk", icon: DesktopWindowsIcon },
];

const useStyles = makeStyles((theme) => ({
	paper: {
		width: 480,
		maxWidth: "100vw",
		display: "flex",
		flexDirection: "column",
	},
	header: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.5),
		padding: theme.spacing(2.5, 3, 2),
	},
	avatar: {
		width: 46,
		height: 46,
		borderRadius: 13,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		color: "#fff",
		fontWeight: 800,
		fontSize: "1.05rem",
		flexShrink: 0,
	},
	headerInfo: { flex: 1, minWidth: 0 },
	body: {
		flex: 1,
		overflowY: "auto",
		padding: theme.spacing(0, 3, 3),
		...theme.scrollbarStyles,
	},
	section: { marginTop: theme.spacing(3) },
	sectionTitle: {
		fontSize: "0.72rem",
		fontWeight: 700,
		textTransform: "uppercase",
		letterSpacing: "0.06em",
		color: theme.custom.textSecondary,
		marginBottom: theme.spacing(1.25),
	},
	moduleGrid: {
		display: "grid",
		gridTemplateColumns: "1fr",
		gap: theme.spacing(1),
	},
	moduleCard: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.5),
		padding: theme.spacing(1.5, 1.75),
		borderRadius: 11,
		border: `1px solid ${theme.custom.border}`,
	},
	moduleOn: {
		borderColor: theme.palette.primary.main,
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(5,150,105,.12)"
				: "rgba(5,150,105,.06)",
	},
	moduleOff: { opacity: 0.55 },
	moduleIcon: { fontSize: 21 },
	moduleName: { flex: 1, fontWeight: 600, fontSize: "0.9rem" },

	limitGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(3, 1fr)",
		gap: theme.spacing(1),
	},
	limitCard: {
		padding: theme.spacing(1.5),
		borderRadius: 11,
		border: `1px solid ${theme.custom.border}`,
		textAlign: "center",
	},
	limitIcon: { fontSize: 18, color: theme.custom.textSecondary },
	limitValue: {
		fontSize: "1.25rem",
		fontWeight: 800,
		lineHeight: 1.1,
		marginTop: 4,
	},
	limitLabel: {
		fontSize: "0.68rem",
		fontWeight: 600,
		color: theme.custom.textSecondary,
		textTransform: "uppercase",
		letterSpacing: "0.04em",
	},
	limitBar: { height: 5, borderRadius: 3, marginTop: 6 },
	limitBarFull: {
		"& .MuiLinearProgress-bar": {
			backgroundColor: theme.palette.warning.main,
		},
	},

	deviceRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.25),
		padding: theme.spacing(1.25, 1.5),
		borderRadius: 11,
		border: `1px solid ${theme.custom.border}`,
		marginBottom: theme.spacing(1),
	},
	deviceInfo: { flex: 1, minWidth: 0 },
	deviceName: {
		fontWeight: 600,
		fontSize: "0.88rem",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	deviceMeta: {
		fontSize: "0.72rem",
		color: theme.custom.textSecondary,
		marginTop: 1,
	},
	emptyBox: {
		padding: theme.spacing(3),
		textAlign: "center",
		borderRadius: 11,
		border: `1px dashed ${theme.custom.border}`,
		color: theme.custom.textSecondary,
		fontSize: "0.85rem",
	},
	loading: { display: "flex", justifyContent: "center", padding: 24 },
	footer: {
		padding: theme.spacing(2, 3),
		borderTop: `1px solid ${theme.custom.border}`,
	},
}));

const AVATAR_COLORS = [
	"#059669", "#0EA5E9", "#8B5CF6", "#F59E0B",
	"#EC4899", "#14B8A6", "#6366F1", "#EF4444",
];
const colorFor = (name) => {
	let h = 0;
	for (let i = 0; i < (name || "").length; i += 1) {
		h = (h * 31 + name.charCodeAt(i)) >>> 0;
	}
	return AVATAR_COLORS[h % AVATAR_COLORS.length];
};
const initialsOf = (name) =>
	(name || "?").split(/\s+/).filter(Boolean).slice(0, 2)
		.map((w) => w[0]).join("").toUpperCase();

const CompanyDetailsDrawer = ({ open, company, onClose, onEdit }) => {
	const classes = useStyles();
	const [devices, setDevices] = useState([]);
	const [loading, setLoading] = useState(false);

	const modules = Array.isArray(company?.modules)
		? company.modules
		: ["whatsapp"];
	const hasFlydesk = modules.includes("flydesk");

	const fetchDevices = useCallback(async () => {
		if (!company?.id || !hasFlydesk) {
			setDevices([]);
			return;
		}
		setLoading(true);
		try {
			const { data } = await api.get("/flydesk/devices", {
				params: { companyId: company.id },
			});
			setDevices(data.devices || []);
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
	}, [company, hasFlydesk]);

	useEffect(() => {
		if (open) fetchDevices();
	}, [open, fetchDevices]);

	if (!company) return null;

	const limits = [
		{
			icon: PeopleAltOutlinedIcon,
			label: i18n.t("companies.details.users"),
			used: company.usersCount,
			max: company.maxUsers,
		},
		{
			icon: SyncAltIcon,
			label: i18n.t("companies.details.connections"),
			used: company.connectionsCount,
			max: company.maxConnections,
		},
		{
			icon: DesktopWindowsIcon,
			label: i18n.t("companies.details.devices"),
			used: company.devicesCount ?? devices.length,
			max: company.maxDevices,
			hidden: !hasFlydesk,
		},
	].filter((l) => !l.hidden);

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			classes={{ paper: classes.paper }}
		>
			<div className={classes.header}>
				<div
					className={classes.avatar}
					style={{ backgroundColor: colorFor(company.name) }}
				>
					{initialsOf(company.name)}
				</div>
				<div className={classes.headerInfo}>
					<Typography variant="h6">{company.name}</Typography>
					<Chip
						size="small"
						label={
							company.status === false
								? i18n.t("companies.table.inactive")
								: i18n.t("companies.table.active")
						}
						color={company.status === false ? "default" : "primary"}
					/>
				</div>
				<IconButton onClick={onClose} size="small">
					<CloseIcon />
				</IconButton>
			</div>

			<Divider />

			<div className={classes.body}>
				<div className={classes.section}>
					<div className={classes.sectionTitle}>
						{i18n.t("companies.details.modulesTitle")}
					</div>
					<div className={classes.moduleGrid}>
						{MODULES.map((m) => {
							const on = modules.includes(m.key);
							const Icon = m.icon;
							return (
								<div
									key={m.key}
									className={`${classes.moduleCard} ${
										on ? classes.moduleOn : classes.moduleOff
									}`}
								>
									<Icon
										className={classes.moduleIcon}
										color={on ? "primary" : "disabled"}
									/>
									<span className={classes.moduleName}>
										{i18n.t(`companies.modal.module_${m.key}`, {
											defaultValue: m.key,
										})}
									</span>
									{on ? (
										<CheckCircleIcon
											style={{ fontSize: 18 }}
											color="primary"
										/>
									) : (
										<RemoveCircleOutlineIcon
											style={{ fontSize: 18 }}
											color="disabled"
										/>
									)}
								</div>
							);
						})}
					</div>
				</div>

				<div className={classes.section}>
					<div className={classes.sectionTitle}>
						{i18n.t("companies.details.limitsTitle")}
					</div>
					<div className={classes.limitGrid}>
						{limits.map((l) => {
							const Icon = l.icon;
							const pct = l.max ? Math.min(100, (l.used / l.max) * 100) : 0;
							const full = Boolean(l.max) && l.used >= l.max;
							return (
								<div key={l.label} className={classes.limitCard}>
									<Icon className={classes.limitIcon} />
									<div className={classes.limitValue}>
										{l.used ?? 0}
										<span
											style={{
												fontSize: "0.8rem",
												fontWeight: 600,
												opacity: 0.6,
											}}
										>
											{l.max ? `/${l.max}` : ""}
										</span>
									</div>
									<div className={classes.limitLabel}>{l.label}</div>
									{l.max ? (
										<LinearProgress
											variant="determinate"
											value={pct}
											className={`${classes.limitBar} ${
												full ? classes.limitBarFull : ""
											}`}
										/>
									) : (
										<div
											className={classes.limitLabel}
											style={{ marginTop: 4, opacity: 0.7 }}
										>
											{i18n.t("companies.details.unlimited")}
										</div>
									)}
								</div>
							);
						})}
					</div>
				</div>

				{hasFlydesk && (
					<div className={classes.section}>
						<div className={classes.sectionTitle}>
							{i18n.t("companies.details.devicesTitle")}
						</div>

						{loading && (
							<div className={classes.loading}>
								<CircularProgress size={26} />
							</div>
						)}

						{!loading && devices.length === 0 && (
							<div className={classes.emptyBox}>
								{i18n.t("companies.details.noDevices")}
							</div>
						)}

						{!loading &&
							devices.map((d) => {
								const online = isOnline(d.lastSeenAt);
								const state = d.blocked
									? "blocked"
									: online
									? "online"
									: "offline";
								return (
									<div key={d.id} className={classes.deviceRow}>
										<StatusDot state={state} />
										<div className={classes.deviceInfo}>
											<div className={classes.deviceName}>
												{d.name || i18n.t("flydeskDevices.unnamed")}
											</div>
											<div className={classes.deviceMeta}>
												{d.os || "—"}
												{d.blocked && ` · ${i18n.t("flydeskDevices.blocked")}`}
											</div>
										</div>
										{/* O ID fica aqui pronto para copiar e colar no
										    Flydesk — e o caminho mais curto entre ver a
										    empresa e conectar no PC dela. */}
										<DeviceId value={d.deviceId} />
									</div>
								);
							})}
					</div>
				)}
			</div>

			<div className={classes.footer}>
				<Button
					fullWidth
					variant="contained"
					color="primary"
					startIcon={<EditIcon />}
					onClick={() => onEdit(company)}
				>
					{i18n.t("companies.details.edit")}
				</Button>
			</div>
		</Drawer>
	);
};

export default CompanyDetailsDrawer;
